import {
  Dialog,
  FlexColumn,
  CodeEditor,
} from '@elementary/common-ui/components';
import { Typography } from '@mui/material';
import styled from 'styled-components';
import { GPGKey } from '../../types';
import GPGKeyDetails from '../GPGKeyDetails';

const InstructionBoxBase = styled(FlexColumn)`
  background-color: ${({ theme }) => theme.palette.common.gray9};
  padding: 16px;
  border-radius: 8px;
  gap: 8px;
  margin-bottom: 16px;
  margin-top: 16px;
`;

const InstructionBoxTitle = styled(Typography)`
  font-size: 12px;
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  color: ${({ theme }) => theme.palette.common.gray12};
`;

const InstructionBoxContent = styled.ol`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.common.gray12};
  white-space: pre;
  margin: 0;
  padding-left: 18px;
  line-height: 20px;
`;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  gpgKey: GPGKey;
}

const SuccessStageDialog = ({ isOpen, onClose, gpgKey }: Props) => (
  <Dialog
    open={isOpen}
    onClose={onClose}
    title="GPG Key Generated Successfully"
    confirmText="Done"
    hideCancelButton
    customWidth={800}
    onConfirmButtonClick={onClose}
    message={
      <FlexColumn sx={{ gap: 3 }}>
        <FlexColumn sx={{ gap: 1 }}>
          <Typography
            variant="body1"
            sx={{ fontWeight: 'bold', color: 'success.main' }}
          >
            ✓ Your GPG key has been generated and is now active!
          </Typography>

          <InstructionBoxBase>
            <InstructionBoxTitle>To enable signed commits:</InstructionBoxTitle>
            <InstructionBoxContent>
              <li>Copy the public key below</li>
              <li> Go to GitHub → Settings → SSH and GPG keys</li>
              <li>Click ‘New GPG key’ and paste the public key</li>
              <li>
                Your future commits will be automatically signed with this key
              </li>
            </InstructionBoxContent>
            <Typography sx={{ fontSize: '12px', color: 'common.gray12' }}>
              Note: Keep this public key safe - you’ll need to add it to any Git
              platforms where you want signed commits to be verified.
            </Typography>
          </InstructionBoxBase>

          <GPGKeyDetails gpgKey={gpgKey} />
        </FlexColumn>

        <FlexColumn sx={{ gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            Public Key:
          </Typography>

          <CodeEditor
            value={gpgKey.publicKey}
            mode="text"
            readOnly
            height="300px"
            hideLineNumbers
          />
        </FlexColumn>
      </FlexColumn>
    }
  />
);

export default SuccessStageDialog;
